import { IOfferExtraBonus } from '@wyz/types';
import { Flex, Icon, Text, Tooltip } from '@wyz/ui';
import { isNil } from 'lodash-es';
import { Trans, useLocale } from '~/common/context';

type Props = {
  id: string;
  extraBonus: IOfferExtraBonus;
};

/**
 * Informative picto shown next to the unit price: hovering it details the bonuses applied to the
 * offer and the net price they lead to. Purely a read of the payload — nothing here feeds the cart.
 */
function OfferExtraBonus({ id, extraBonus }: Props) {
  const locale = useLocale();

  const money = (value: number) =>
    locale.formatters.money(value, {
      currency: extraBonus.currency,
      fallback: '-',
    });

  const bonusValue = (value: number) => {
    if (extraBonus.bonusType === 'NET_MARGIN') {
      return money(value);
    }
    return (
      <Trans
        i18nKey={
          extraBonus.bonusType === 'DISCOUNT_BF'
            ? 'app.purchase.extra_bonus.value.discount_bf'
            : 'app.purchase.extra_bonus.value.margin'
        }
        values={{
          value: String(value),
        }}
      />
    );
  };

  return (
    <Tooltip
      label={
        <Flex direction='column' id={`${id}-details`}>
          {!isNil(extraBonus.hqBonus) && (
            <span id={`${id}-hq-bonus`}>
              <Trans i18nKey='app.purchase.extra_bonus.hq_bonus' />{' '}
              {bonusValue(extraBonus.hqBonus)}
            </span>
          )}
          {!isNil(extraBonus.extraBonus) && (
            <span id={`${id}-bonus`}>
              <Trans i18nKey='app.purchase.extra_bonus.bonus' />{' '}
              {bonusValue(extraBonus.extraBonus)}
            </span>
          )}
          <span id={`${id}-net-price`}>
            <Trans
              i18nKey='app.purchase.extra_bonus.net_price'
              values={{ value: money(extraBonus.netPrice) }}
            />
          </span>
        </Flex>
      }
    >
      <Text component='span' color='primary' className='d-inline-flex'>
        <Icon id={id} icon='coins-line' size='xs' />
      </Text>
    </Tooltip>
  );
}

export default OfferExtraBonus;
